import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { leaveRequestsService } from '../../services/api/leaveRequests';
import { useActiveLeaveTypes } from '../../hooks/useLeaveTypes';
import { useMyLeaveBalance } from '../../hooks/useLeaveBalance';
import { HalfDayType } from '../../types';
import DateRangePicker from '../common/DateRangePicker';
import Loading from '../common/Loading';
import { useToast } from '../../contexts/ToastContext';
import './LeaveRequestForm.css';

const leaveRequestSchema = z.object({
  leaveTypeId: z.number().min(1, 'Please select a leave type'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  halfDayType: z.nativeEnum(HalfDayType).optional(),
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
}).refine((data) => data.startDate <= data.endDate, {
  message: 'End date must be after start date',
  path: ['endDate'],
});

type LeaveRequestFormData = z.infer<typeof leaveRequestSchema>;

const LeaveRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { leaveTypes, loading: loadingTypes } = useActiveLeaveTypes();
  const { balance, loading: loadingBalance } = useMyLeaveBalance();
  const [submitting, setSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState<'draft' | 'submit'>('draft');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LeaveRequestFormData>({
    resolver: zodResolver(leaveRequestSchema),
    defaultValues: {
      leaveTypeId: 0,
      startDate: '',
      endDate: '',
      halfDayType: HalfDayType.NONE,
      reason: '',
    },
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const halfDayType = watch('halfDayType');

  // Check if it's a single day (startDate === endDate)
  const isSingleDay = startDate && endDate && startDate === endDate;


  // Reset halfDayType to NONE when dates change to multi-day
  useEffect(() => {
    if (!isSingleDay && halfDayType && halfDayType !== HalfDayType.NONE) {
      setValue('halfDayType', HalfDayType.NONE);
    }
  }, [isSingleDay, halfDayType, setValue]);

  const onSubmit = async (data: LeaveRequestFormData) => {
    try {
      setSubmitting(true);

      // Create the leave request
      const newRequest = await leaveRequestsService.create({
        leaveTypeId: data.leaveTypeId,
        startDate: data.startDate,
        endDate: data.endDate,
        halfDayType: data.halfDayType,
        reason: data.reason,
      });

      // If user clicked "Submit", also submit it for approval
      if (submitAction === 'submit') {
        await leaveRequestsService.submit(newRequest.id);
        showSuccess('Leave request created and submitted for approval!');
      } else {
        showSuccess('Leave request saved as draft successfully!');
      }

      // Navigate to my requests page
      navigate('/leave-requests/me');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to create leave request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingTypes || loadingBalance) {
    return <Loading text="Loading form data..." />;
  }

  return (
    <div className="leave-request-form-container">
      <h2>New Leave Request</h2>

      {balance && (
        <div className="leave-balance-info">
          <h3>Your Leave Balance</h3>
          <div className="balance-grid">
            <div className="balance-item">
              <span className="balance-label">Accrued</span>
              <span className="balance-value">{balance.accruedDays} days</span>
            </div>
            <div className="balance-item">
              <span className="balance-label">Used</span>
              <span className="balance-value">{balance.usedDays} days</span>
            </div>
            <div className="balance-item balance-item-primary">
              <span className="balance-label">Remaining</span>
              <span className="balance-value">{balance.remainingDays} days</span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="leave-request-form">
        <div className="form-group">
          <label htmlFor="leaveTypeId">Leave Type *</label>
          <select
            id="leaveTypeId"
            {...register('leaveTypeId', { valueAsNumber: true })}
            className={errors.leaveTypeId ? 'input-error' : ''}
          >
            <option value={0}>Select leave type...</option>
            {leaveTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} {type.isPaid ? '(Paid)' : '(Unpaid)'}
              </option>
            ))}
          </select>
          {errors.leaveTypeId && (
            <p className="error-message">{errors.leaveTypeId.message}</p>
          )}
        </div>

        <DateRangePicker
          label="Leave Period *"
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={(date) => setValue('startDate', date)}
          onEndDateChange={(date) => setValue('endDate', date)}
          error={errors.startDate?.message || errors.endDate?.message}
        />

        {isSingleDay && (
          <div className="form-group">
            <label htmlFor="halfDayType">Half Day</label>
            <select
              id="halfDayType"
              {...register('halfDayType')}
              className={errors.halfDayType ? 'input-error' : ''}
            >
              <option value={HalfDayType.NONE}>Full Day</option>
              <option value={HalfDayType.MORNING}>Morning (08:30 - 12:00)</option>
              <option value={HalfDayType.AFTERNOON}>Afternoon (13:30 - 18:00)</option>
            </select>
            {errors.halfDayType && (
              <p className="error-message">{errors.halfDayType.message}</p>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="reason">Reason *</label>
          <textarea
            id="reason"
            {...register('reason')}
            rows={4}
            placeholder="Please provide a reason for your leave request..."
            className={errors.reason ? 'input-error' : ''}
          />
          {errors.reason && (
            <p className="error-message">{errors.reason.message}</p>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/leave-requests/me')}
            className="button button-secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={() => setSubmitAction('draft')}
            className="button button-outline"
            disabled={submitting}
          >
            {submitting && submitAction === 'draft' ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="submit"
            onClick={() => setSubmitAction('submit')}
            className="button button-primary"
            disabled={submitting}
          >
            {submitting && submitAction === 'submit' ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeaveRequestForm;
